import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, FlatList, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, Image} from 'react-native';
import {
  Text, TextInput, Button, Appbar, Chip, Avatar, Divider, ActivityIndicator, Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {useConfigStore} from '../../store/useConfigStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {measurementApi} from '../../services/api/measurementApi';
import {customerApi} from '../../services/api/customerApi';
import {colors, spacing} from '../../theme';

const MeasurementFormScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId} = route.params;
  const preselectedCatalogItemId = route.params?.catalogItemId;
  const editProfileId = route.params?.profileId;
  const returnTo = route.params?.returnTo;
  const paramMode = route.params?.mode;
  const originalProfileName = route.params?.originalProfileName;

  const {catalogItems, fetchCatalogItems, createProfile, updateProfile} = useMeasurementStore();

  const isEditMode = paramMode === 'edit' || !!editProfileId;
  const [step, setStep] = useState(preselectedCatalogItemId || isEditMode ? 2 : 1);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [measurementOptions, setMeasurementOptions] = useState([]);
  const [values, setValues] = useState({});
  const [profileName, setProfileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingFields, setLoadingFields] = useState(isEditMode);
  const [snackbar, setSnackbar] = useState('');
  const [customerGender, setCustomerGender] = useState(route.params?.customerGender || null);

  const {fetchByType, getProperty} = useConfigStore();
  const {orgUnits, selectedOrgUnitId, fetchOrgUnits} = useOrgUnitStore();
  const effectiveOrgUnitId = orgUnits.length === 1 ? orgUnits[0].id : selectedOrgUnitId;

  useEffect(() => {
    if (orgUnits.length === 0) fetchOrgUnits();
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (effectiveOrgUnitId) fetchByType('ORDER', effectiveOrgUnitId);
  }, [effectiveOrgUnitId, fetchByType]);

  const measurementUnit = getProperty('ORDER', 'MEASUREMENT_UNIT', effectiveOrgUnitId) || 'INCHES';
  const unitLabel = measurementUnit === 'CMS' ? 'cms' : 'in';

  useEffect(() => {
    if (!isEditMode) fetchCatalogItems();
  }, [isEditMode, fetchCatalogItems]);

  // Fetch customer gender if not passed as param
  useEffect(() => {
    if (!customerGender && customerId) {
      customerApi.getById(customerId).then(res => {
        setCustomerGender(res.data.data?.gender || null);
      }).catch(() => {});
    }
  }, [customerId, customerGender]);

  // Build a unique key for a measurement option
  const optionKey = (item) =>
    item.globalOptionId ? String(item.globalOptionId) : 'custom_' + item.customOptionId;

  // Edit mode: fetch existing profile and measurement options
  useEffect(() => {
    if (!isEditMode || !editProfileId) return;

    const loadProfile = async () => {
      setLoadingFields(true);
      try {
        const profileRes = await measurementApi.getProfile(customerId, editProfileId);
        const profile = profileRes.data.data;

        setProfileName(profile.profileName || '');

        const dtId = profile.catalogItemId || preselectedCatalogItemId;

        // Fetch catalog item info for display
        const catalogItemRes = await measurementApi.getCatalogItem(dtId);
        const catalogItem = catalogItemRes.data.data;
        if (catalogItem) setSelectedCatalogItem(catalogItem);

        // Fetch measurement options — only assigned (ACTIVE) ones for ordering
        const mvRes = await measurementApi.getMeasurementValues(dtId);
        const options = (mvRes.data.data || []).filter(item => item.isEnabled);
        setMeasurementOptions(options);

        // Prefill values from profile
        const prefilled = options.reduce((acc, item) => ({...acc, [optionKey(item)]: ''}), {});
        if (profile.values) {
          profile.values.forEach(v => {
            const key = v.globalOptionId ? String(v.globalOptionId) : v.customOptionId ? 'custom_' + v.customOptionId : null;
            if (key && prefilled.hasOwnProperty(key)) {
              prefilled[key] = v.value?.toString() || '';
            }
          });
        }
        setValues(prefilled);
      } catch (e) {
        setSnackbar(e.response?.data?.error?.message || t('common.error'));
      } finally {
        setLoadingFields(false);
      }
    };
    loadProfile();
  }, [isEditMode, editProfileId, customerId, preselectedCatalogItemId, t]);

  // Create mode with preselected catalog item
  useEffect(() => {
    if (!isEditMode && preselectedCatalogItemId && catalogItems.length > 0) {
      const dt = catalogItems.find(d => d.id === preselectedCatalogItemId);
      if (dt) {
        selectCatalogItem(dt);
      }
    }
  }, [isEditMode, preselectedCatalogItemId, catalogItems]);

  const selectCatalogItem = useCallback(async dt => {
    setSelectedCatalogItem(dt);
    setLoadingFields(true);
    try {
      const mvRes = await measurementApi.getMeasurementValues(dt.id);
      const options = (mvRes.data.data || []).filter(item => item.isEnabled);
      setMeasurementOptions(options);
      setValues(options.reduce((acc, item) => ({...acc, [optionKey(item)]: ''}), {}));
      setProfileName(`${dt.name} - ${new Date().toLocaleDateString()}`);
      setStep(2);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    } finally {
      setLoadingFields(false);
    }
  }, [t]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const measurementValues = Object.entries(values)
        .filter(([_, v]) => v.toString().trim() !== '')
        .map(([key, value]) => {
          if (key.startsWith('custom_')) {
            return {customOptionId: Number(key.replace('custom_', '')), value: value.toString().trim()};
          }
          return {globalOptionId: Number(key), value: value.toString().trim()};
        });

      const payload = {
        catalogItemId: selectedCatalogItem.id,
        profileName,
        measuredAt: new Date().toISOString().split('T')[0],
        values: measurementValues,
      };

      let savedProfile;
      if (isEditMode && originalProfileName && profileName === originalProfileName) {
        savedProfile = await updateProfile(customerId, editProfileId, payload);
      } else if (isEditMode && originalProfileName && profileName !== originalProfileName) {
        savedProfile = await createProfile(customerId, payload);
      } else if (isEditMode) {
        savedProfile = await updateProfile(customerId, editProfileId, payload);
      } else {
        savedProfile = await createProfile(customerId, payload);
      }

      if (returnTo === 'OrderLineDetail' && savedProfile) {
        navigation.popTo('OrderLineDetail', {
          measurementProfileId: savedProfile.id,
          measurementProfileName: savedProfile.profileName,
        }, { merge: true });
      } else {
        navigation.goBack();
      }
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [values, selectedCatalogItem, profileName, customerId, editProfileId, isEditMode, originalProfileName, returnTo, createProfile, updateProfile, navigation, t]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => step > 1 && !preselectedCatalogItemId && !isEditMode ? setStep(1) : navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title={isEditMode ? t('measurement.editMeasurement') : t('measurement.title')}
          titleStyle={styles.appbarTitle}
        />
      </Appbar.Header>

      {step === 1 && (
        catalogItems.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <FlatList
            data={catalogItems}
            keyExtractor={item => String(item.id)}
            renderItem={({item}) => (
              <TouchableOpacity onPress={() => selectCatalogItem(item)} activeOpacity={0.7}>
                <View style={styles.catalogRow}>
                  {item.mainImageUrl ? (
                    <View style={styles.catalogAvatarContainer}>
                      <Image source={{uri: item.mainImageUrl}} style={styles.catalogAvatarImage} />
                    </View>
                  ) : (
                    <Avatar.Text size={40} label={item.name.charAt(0).toUpperCase()} style={styles.catalogAvatar} />
                  )}
                  <Text variant="bodyLarge" style={styles.catalogItemName}>{item.name}</Text>
                  <Text style={styles.catalogChevron}>{'>'}</Text>
                </View>
                <Divider />
              </TouchableOpacity>
            )}
            contentContainerStyle={styles.catalogListContent}
          />
        )
      )}

      {step === 2 && (
      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.content} keyboardShouldPersistTaps="handled">
        {loadingFields ? (
          <ActivityIndicator size="large" color={colors.primary} style={{marginTop: spacing.xxl}} />
        ) : (
          <>
            <Chip style={styles.selectedDt}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{selectedCatalogItem?.name}</Text></Chip>

            <View style={styles.unitBanner}>
              <Text style={styles.unitBannerText}>All measurements in {measurementUnit === 'CMS' ? 'Centimeters (cms)' : 'Inches (in)'}</Text>
            </View>

            <TextInput
              label={t('measurement.profileName')}
              value={profileName}
              onChangeText={setProfileName}
              mode="outlined"
              style={styles.input}
              outlineColor={colors.border}
              activeOutlineColor={colors.primary}
            />

            {measurementOptions.map(item => {
              const imgUrl = customerGender === 'MALE' ? item.imageUrl1 : item.imageUrl2;
              return (
                <View key={optionKey(item)} style={styles.measurementRow}>
                  {imgUrl ? (
                    <Image source={{uri: imgUrl}} style={styles.measurementImage} />
                  ) : (
                    <View style={styles.measurementImageEmpty} />
                  )}
                  <TextInput
                    label={`${item.globalOptionName || item.customOptionName} (${unitLabel})`}
                    value={values[optionKey(item)]?.toString() || ''}
                    onChangeText={v => setValues(prev => ({...prev, [optionKey(item)]: v}))}
                    mode="outlined"
                    style={styles.measurementInput}
                    keyboardType="numeric"
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                  />
                </View>
              );
            })}

            <Button
              mode="contained"
              onPress={handleSave}
              loading={saving}
              disabled={saving}
              style={styles.saveButton}
              buttonColor={colors.primary}>
              {returnTo ? 'Save & Continue' : t('common.save')}
            </Button>
          </>
        )}
      </ScrollView>
      </KeyboardAvoidingView>
      )}

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  content: {flex: 1, padding: spacing.md},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  catalogListContent: {paddingBottom: spacing.xxl},
  catalogRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  catalogAvatar: {backgroundColor: colors.primary + '30', marginRight: spacing.md},
  catalogAvatarContainer: {
    width: 40, height: 40, borderRadius: 20, marginRight: spacing.md,
    padding: 4, justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: colors.border,
  },
  catalogAvatarImage: {width: 32, height: 32, borderRadius: 16},
  catalogItemName: {flex: 1, color: colors.text},
  catalogChevron: {color: colors.textSecondary, fontSize: 18},
  selectedDt: {alignSelf: 'flex-start', marginBottom: spacing.sm, backgroundColor: colors.primary + '20'},
  unitBanner: {
    backgroundColor: colors.primary + '12', borderRadius: 6,
    paddingVertical: spacing.xs, paddingHorizontal: spacing.sm, marginBottom: spacing.sm,
  },
  unitBannerText: {fontSize: 13, fontWeight: '600', color: colors.primary, textAlign: 'center'},
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  measurementRow: {
    flexDirection: 'row',
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  measurementImage: {
    width: 100,
    height: 100,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
  measurementImageEmpty: {
    width: 100,
    height: 100,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#CCC',
    backgroundColor: colors.background,
  },
  measurementInput: {
    flex: 1,
    backgroundColor: colors.surface,
    alignSelf: 'center',
  },
  saveButton: {marginTop: spacing.md, marginBottom: spacing.xxl, borderRadius: 8},
});

export default MeasurementFormScreen;
